import React, {useEffect, useState} from "react";
import {Button, Card, Chip, Grid, Stack, Typography} from "@mui/joy";
import IProfile from "../../types/profile.type";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/ThemeProviderWithVars";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {useFormik} from "formik";
import moment, {Moment} from "moment/moment";
import * as Yup from "yup";
import {updateEmploymentData} from "../../services/profile.service";
import {getById} from "../../services/file.service";
import showNotification from "../utils/Notify";
import WorkIcon from "@mui/icons-material/Work";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import IEmploymentData from "../../types/employment-data.type";
import CancelIcon from "@mui/icons-material/Cancel";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import IconButton from "@mui/joy/IconButton";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import IFile from "../../types/file.type";
import {downloadFile} from "../../services/file-download.service";

interface Props {
  userId: any,
  profile: IProfile | undefined,
  canEdit: boolean,
}

const EmploymentDataForm: React.FC<Props> = (props) => {
  const { profile, canEdit } = props;
  const [edit, setEdit] = useState(false);
  // const [files, setFiles] = useState<File[]>([]);

  const initialValues: IEmploymentData = {
    initiation: null,
    termination: null,
    initiationFile: null,
    terminationFile: null,
    uploadInitiationFile: null,
    uploadTerminationFile: null,
  };

  const validationSchema = Yup.object({
    initiation: Yup.date().required('La Fecha de Alta es requerida')
  });

  const onSubmit = (employmentData: IEmploymentData) => {
    if (profile?.id) {
      updateEmploymentData(profile?.id, employmentData)
        .then((response) => {
          showNotification({type: 'success', message: 'Se actualizaron los Datos Laborales.'});
          profile.employmentData = response.data.employmentData;
          formik.setFieldValue('uploadInitiationFile', null);
          formik.setFieldValue('uploadTerminationFile', null);
          setEdit(false);
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleChangeInitiation = (value: Moment | null) => {
    formik.setFieldValue('initiation', moment(value))
  };

  const handleChangeTermination = (value: Moment | null) => {
    formik.setFieldValue('termination', value ? moment(value) : null)
  };

  const handleEditCancelClick = () => {
    formik.setTouched({});
    if (edit) {
      setEdit(false);
      if (profile?.employmentData) {
        formik.setValues(profile.employmentData);
      } else {
        formik.resetForm();
      }
    } else {
      setEdit(true);
    }
  };

  useEffect(() => {
    if (profile?.employmentData) {
      formik.setValues(profile.employmentData);
      formik.setFieldValue('initiation', moment(profile.employmentData.initiation).set({hour: 14}));
      if(profile.employmentData.termination) {
        formik.setFieldValue('termination', moment(profile.employmentData.termination).set({hour: 14}));
      }
    }
  }, [profile?.id]);

  return (
    <Card sx={{gap: 0, marginTop: 2}}>
      <Typography level="h4" startDecorator={<WorkIcon/>}>Datos Laborales</Typography>
      <form className="form" onSubmit={formik.handleSubmit}>
        <Grid container rowSpacing={2} columnSpacing={2} marginBlock={1}>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  name="initiation"
                  label="Fecha de Alta"
                  format="DD/MM/YYYY"
                  value={formik.values.initiation ? moment(formik.values.initiation) : null}
                  onChange={handleChangeInitiation}
                  readOnly={!edit}
                  slotProps={{
                    textField: {
                      variant: 'filled',
                      fullWidth: true,
                      InputLabelProps: {
                        shrink: true
                      }
                    },
                  }}/>
              </LocalizationProvider>
              {formik.touched.initiation && formik.errors.initiation ? (
                <span className="Error-alert">{String(formik.errors.initiation)}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <Card sx={{height: 57, padding: 1, margin:0}}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography sx={{fontWeight: "bold"}} pt={1} pr={1}>
                    Alta de AFIP:
                  </Typography>
                  {formik.values.uploadInitiationFile ?
                    <Chip
                      variant="outlined"
                      color="neutral"
                      size="lg"
                    >
                      {formik.values.uploadInitiationFile.name}
                    </Chip>
                    :
                      profile?.employmentData?.initiationFile ?
                        <Chip
                          variant="outlined"
                          color="neutral"
                          size="lg"
                          onClick={() => downloadFile(profile?.employmentData?.initiationFile)}
                          endDecorator={<DownloadForOfflineIcon sx={{ fontSize: 30}}/>}
                        >
                          {profile.employmentData.initiationFile.fileName}
                        </Chip>
                      :
                        <Typography pt={1} pr={1}>
                          No hay archivo adjunto
                        </Typography>
                  }
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <input
                    id="uploadInitiationFile"
                    name="uploadInitiationFile"
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{display: 'none'}}
                    onChange={(event) => {
                      if (event.currentTarget.files && event.currentTarget.files.length > 0) {
                        const newFile = event.currentTarget.files[0];
                        formik.setFieldValue("uploadInitiationFile", newFile);
                      }
                    }}
                  />
                  { canEdit &&
                    <IconButton disabled={!edit} onClick={() => document.getElementById('uploadInitiationFile')?.click()}>
                      <CloudUploadIcon sx={{ fontSize: 40}} />
                    </IconButton>
                  }
                </Stack>
              </Stack>
            </Card>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  name="termination"
                  label="Fecha de Baja"
                  format="DD/MM/YYYY"
                  value={formik.values.termination ? moment(formik.values.termination) : null}
                  onChange={handleChangeTermination}
                  readOnly={!edit}
                  slotProps={{
                    textField: {
                      variant: 'filled',
                      fullWidth: true,
                      InputLabelProps: {
                        shrink: true,
                      }
                    },
                    field: { clearable: true }
                  }}/>
              </LocalizationProvider>
              {(formik.touched.termination && formik.errors.termination) && edit ? (
                <span className="Error-alert">{String(formik.errors.termination)}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <Card sx={{height: 57, padding: 1, margin:0}}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography sx={{fontWeight: "bold"}} pt={1} pr={1}>
                    Baja de AFIP:
                  </Typography>
                  {formik.values.uploadTerminationFile ?
                    <Chip
                      variant="outlined"
                      color="neutral"
                      size="lg"
                    >
                      {formik.values.uploadTerminationFile.name}
                    </Chip>
                    :
                    profile?.employmentData?.terminationFile ?
                      <Chip
                        variant="outlined"
                        color="neutral"
                        size="lg"
                        onClick={() => downloadFile(profile?.employmentData?.terminationFile)}
                        endDecorator={<DownloadForOfflineIcon sx={{ fontSize: 30}}/>}
                      >
                        {profile.employmentData.terminationFile.fileName}
                      </Chip>
                      :
                      <Typography pt={1} pr={1}>
                        No hay archivo adjunto
                      </Typography>
                  }
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <input
                    id="uploadTerminationFile"
                    name="uploadTerminationFile"
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{display: 'none'}}
                    onChange={(event) => {
                      if (event.currentTarget.files && event.currentTarget.files.length > 0) {
                        const newFile = event.currentTarget.files[0];
                        formik.setFieldValue("uploadTerminationFile", newFile);
                      }
                    }}
                  />
                  { canEdit &&
                    <IconButton disabled={!edit} onClick={() => document.getElementById('uploadTerminationFile')?.click()}>
                      <CloudUploadIcon sx={{ fontSize: 40}} />
                    </IconButton>
                  }
                </Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        { canEdit &&
          <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
            <Button variant="outlined" endDecorator={edit ? <CancelIcon/> : <BorderColorIcon />} onClick={handleEditCancelClick}>{edit ? 'Cancelar' : 'Editar'}</Button>
            <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon/>} disabled={!edit}>Guardar</Button>
          </Stack>
        }
      </form>
    </Card>
  );
};

export default EmploymentDataForm;
